import {
  Badge,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { IImportCsvRow } from '../utils/csvHelpers';

export interface ImportRowResultsProps {
  rows: readonly IImportCsvRow[];
  messages?: readonly string[];
  createdCount?: number;
  updatedCount?: number;
  errorsCount?: number;
}

type ImportKind = 'created' | 'updated' | 'error';

const KIND_BADGE = {
  created: 'success',
  updated: 'warning',
  error: 'danger',
} as const;

const KIND_LABEL = {
  created: 'Added',
  updated: 'Updated',
  error: 'Error',
} as const;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalS,
  },
  stats: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  extraMessages: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  rowList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  rowCreated: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  rowUpdated: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
  },
  rowError: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
  },
  createdMessage: {
    color: tokens.colorPaletteGreenForeground1,
  },
  updatedMessage: {
    color: tokens.colorPaletteYellowForeground2,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
  },
});

const messageKind = (msg: string): ImportKind | null => {
  switch (msg.trim().toUpperCase()) {
    case 'ADD':
    case 'ADDED':
    case 'CREATED':
      return 'created';
    case 'UPDATE':
    case 'UPDATED':
      return 'updated';
    default:
      return null;
  }
};

const rowKind = (row: IImportCsvRow): ImportKind => {
  if (row.errorsCount > 0) return 'error';
  if ((row.createdCount ?? 0) > 0) return 'created';
  if ((row.updatedCount ?? 0) > 0) return 'updated';
  for (const msg of row.messages) {
    const kind = messageKind(msg);
    if (kind) return kind;
  }
  return 'created';
};

const rowTitle = (row: IImportCsvRow) =>
  typeof row.createdCount === 'number' && typeof row.updatedCount === 'number'
    ? `Row ${row.rowId} — Added: ${row.createdCount}, Updated: ${row.updatedCount}, Error: ${row.errorsCount}`
    : `Row ${row.rowId}`;

const displayMessage = (msg: string, kind: ImportKind) =>
  kind === 'error' ? msg : KIND_LABEL[messageKind(msg) ?? kind];

export const ImportRowResults = ({
  rows,
  messages = [],
  createdCount,
  updatedCount,
  errorsCount,
}: ImportRowResultsProps) => {
  const styles = useStyles();
  const showStats =
    createdCount != null || updatedCount != null || errorsCount != null;
  const rowKindClass: Record<ImportKind, string> = {
    created: styles.rowCreated,
    updated: styles.rowUpdated,
    error: styles.rowError,
  };
  const messageClass: Record<ImportKind, string> = {
    created: styles.createdMessage,
    updated: styles.updatedMessage,
    error: styles.errorMessage,
  };

  return (
    <div className={styles.root}>
      {showStats ? (
        <div className={styles.stats}>
          <Badge appearance="tint" color={KIND_BADGE.created} size="large">
            {KIND_LABEL.created} {createdCount ?? 0}
          </Badge>
          <Badge appearance="tint" color={KIND_BADGE.updated} size="large">
            {KIND_LABEL.updated} {updatedCount ?? 0}
          </Badge>
          <Badge appearance="tint" color={KIND_BADGE.error} size="large">
            {KIND_LABEL.error} {errorsCount ?? 0}
          </Badge>
        </div>
      ) : null}
      {messages.length > 0 ? (
        <div className={styles.extraMessages}>
          {messages.map((msg, idx) => (
            <Text key={`top-${idx}`} size={200}>
              {msg}
            </Text>
          ))}
        </div>
      ) : null}
      <div>
        <Text weight="semibold">Results</Text>
        <div className={styles.rowList}>
          {rows.map((row) => {
            const kind = rowKind(row);
            return (
              <div
                key={row.rowId}
                className={mergeClasses(styles.row, rowKindClass[kind])}
              >
                <Text size={200} weight="semibold">
                  {rowTitle(row)}
                </Text>
                {(row.messages.length > 0
                  ? row.messages
                  : [KIND_LABEL[kind]]
                ).map((msg, idx) => (
                  <Text
                    key={`${row.rowId}-${idx}`}
                    size={200}
                    className={messageClass[kind]}
                  >
                    {displayMessage(msg, kind)}
                  </Text>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ImportRowResults;
